import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function readCssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function useChartColors() {
  const [colors, setColors] = useState(() => ({
    imp: "#002FA7",
    exp: "#0284C7",
    grid: "#e4e4e7",
    axis: "#52525b",
    bg: "#ffffff",
    border: "#e4e4e7",
    primary: "hsl(222 100% 33%)",
  }));

  useEffect(() => {
    const update = () =>
      setColors({
        imp: readCssVar("--doe-import", "#002FA7"),
        exp: readCssVar("--doe-export", "#0284C7"),
        grid: readCssVar("--grid-line", "#e4e4e7"),
        axis: readCssVar("--axis-text", "#52525b"),
        bg: `hsl(${readCssVar("--background", "0 0% 100%")})`,
        border: `hsl(${readCssVar("--border", "240 6% 90%")})`,
        primary: `hsl(${readCssVar("--primary", "222 100% 33%")})`,
      });
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return colors;
}

export default function BenchmarkChart({ rows }) {
  const c = useChartColors();

  // Filter completed runs for comparison
  const completedRows = rows.filter((r) => r.status === "complete");

  if (completedRows.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6" data-testid="benchmark-charts">
      {/* Solve Time Chart */}
      <div className="border border-border rounded-[2px] bg-background p-4 sm:p-6 flex flex-col">
        <h4 className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-4">
          Solve Time (seconds) · lower is better
        </h4>
        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={completedRows}
              layout="vertical"
              margin={{ top: 8, right: 16, left: 16, bottom: 8 }}
            >
              <CartesianGrid stroke={c.grid} strokeDasharray="2 4" horizontal={false} vertical={true} />
              <XAxis
                type="number"
                tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis }}
                tickLine={false}
                axisLine={{ stroke: c.grid }}
                label={{
                  value: "Seconds",
                  position: "insideBottomRight",
                  offset: -5,
                  style: { fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis },
                }}
              />
              <YAxis
                type="category"
                dataKey="methodLabel"
                tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: c.axis }}
                tickLine={false}
                axisLine={{ stroke: c.grid }}
                width={140}
              />
              <Tooltip
                contentStyle={{
                  fontFamily: "IBM Plex Mono",
                  fontSize: 11,
                  borderRadius: 2,
                  border: `1px solid ${c.border}`,
                  background: c.bg,
                }}
                formatter={(v) => [`${Number(v).toFixed(3)}s`, "Solve Time"]}
                labelFormatter={(l) => `${l}`}
              />
              <Bar
                dataKey="solveTimeSeconds"
                fill={c.primary}
                radius={[0, 2, 2, 0]}
                maxBarSize={24}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Energy Capacity Chart */}
      <div className="border border-border rounded-[2px] bg-background p-4 sm:p-6 flex flex-col">
        <h4 className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-4">
          Net Energy Capacity (MWh) · higher is better
        </h4>
        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={completedRows}
              layout="vertical"
              margin={{ top: 8, right: 16, left: 16, bottom: 8 }}
            >
              <CartesianGrid stroke={c.grid} strokeDasharray="2 4" horizontal={false} vertical={true} />
              <XAxis
                type="number"
                tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis }}
                tickLine={false}
                axisLine={{ stroke: c.grid }}
                label={{
                  value: "MWh",
                  position: "insideBottomRight",
                  offset: -5,
                  style: { fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis },
                }}
              />
              <YAxis
                type="category"
                dataKey="methodLabel"
                tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: c.axis }}
                tickLine={false}
                axisLine={{ stroke: c.grid }}
                width={140}
              />
              <Tooltip
                contentStyle={{
                  fontFamily: "IBM Plex Mono",
                  fontSize: 11,
                  borderRadius: 2,
                  border: `1px solid ${c.border}`,
                  background: c.bg,
                }}
                formatter={(v, name) => [
                  `${Number(v).toFixed(2)} MWh`,
                  name === "netImportMwh" ? "Net Import" : "Net Export",
                ]}
                labelFormatter={(l) => `${l}`}
              />
              <Legend
                wrapperStyle={{
                  fontFamily: "IBM Plex Sans",
                  fontSize: 11,
                  paddingTop: 8,
                  color: c.axis,
                }}
                formatter={(value) =>
                  value === "netImportMwh" ? "Net Import (MWh)" : "Net Export (MWh)"
                }
              />
              <Bar
                dataKey="netImportMwh"
                fill={c.imp}
                radius={[0, 2, 2, 0]}
                maxBarSize={16}
              />
              <Bar
                dataKey="netExportMwh"
                fill={c.exp}
                radius={[0, 2, 2, 0]}
                maxBarSize={16}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
